<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import {
  slugList,
  slugCreate,
  slugPatch,
  slugDel,
} from "@/common/api/rest/console/slug";

import type { SlugType } from "@/common/types/slug";

import FormSlug from "@/components/forms/form-slug.vue";
import { baseConfirm, svgIcon } from "@/common/components";

const { t } = useI18n();

const props = defineProps<{
  ownerId: string;
  type: string | null;
}>();

const selected = ref<SlugType | null>(null);
const list = ref<SlugType[]>([]);

const showModalAdd = ref(false);
const showModalDel = ref(false);

const disableIsMain = computed(
  () => list.value.filter((item) => item.is_main).length,
);

function fetchData() {
  if (!props.ownerId) return;

  slugList(props.ownerId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        list.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
}

function addItemConfirm() {
  slugCreate(props.ownerId, {
    is_main: 0,
    language: null,
    slug: null,
    type: props.type,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        addItemCancel();
        fetchData();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function delItem(data: SlugType) {
  showModalDel.value = true;
  selected.value = data;
}

function delItemCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function delItemConfirm() {
  slugDel(selected.value?.slug_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        delItemCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchItem(slug_id: string, payload: Partial<SlugType>) {
  slugPatch(slug_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (!success) {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeIsMain(slug_id: string, payload: Partial<SlugType>) {
  slugPatch(slug_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="list-data">
    <section class="header">
      <div class="title">
        {{ t("slug.labels.slugs") }}
      </div>
    </section>

    <section class="list">
      <template v-for="item in list" :key="item.slug_id">
        <div class="list-actions">
          <button
            type="button"
            class="btn btn-sm btn-danger px-3 py-2"
            @click="delItem(item)"
          >
            <svg-icon icon="trash" size="1.25rem" />
            {{ t("common.labels.delete") }}
          </button>
        </div>
        <form-slug
          :data="item"
          :owner-id="props.ownerId"
          :type="props.type"
          :is-patch="true"
          :disable-is-main="disableIsMain && !item.is_main"
          @update:data="patchItem(item.slug_id, $event)"
          @update:isMain="changeIsMain(item.slug_id, $event)"
        />
        <div class="separator" />
      </template>

      <template v-if="(list || []).length === 0">
        <div class="empty">
          {{ t("slug.texts.empty") }}
        </div>
      </template>

      <div class="actions">
        <button
          type="button"
          class="btn btn-md btn-default uppercase px-4"
          @click="addItem()"
        >
          {{ t("slug.labels.add_slug") }}
        </button>
      </div>
    </section>

    <base-confirm
      :show="showModalAdd"
      :title="t('slug.labels.add_slug').toUpperCase()"
      :message="t('slug.texts.add_slug')"
      @cancel="addItemCancel()"
      @confirm="addItemConfirm()"
    />

    <base-confirm
      :show="showModalDel"
      :title="t('slug.labels.delete_slug').toUpperCase()"
      :message="t('slug.texts.delete_slug')"
      @cancel="delItemCancel()"
      @confirm="delItemConfirm()"
    />
  </article>
</template>

<style lang="scss" scoped>
.list-data {
  display: flex;
  flex-direction: column;
  width: inherit;
  gap: 1rem;

  .header {
    display: flex;
    flex-direction: column;
    width: inherit;
    gap: 0.5rem;

    .title {
      font-size: 1.2rem;
      text-transform: uppercase;
    }
  }

  .list {
    display: flex;
    flex-direction: column;
    padding-inline: 0.5rem;

    &-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 0.5rem;
    }

    .empty {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 1rem;
    }

    .actions {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 1rem;
    }
  }

  .separator {
    height: 1px;
    background: var(--white-off);
    margin-block: 1.54rem;
  }
}
</style>
